export type AdminApiErrorPayload = {
  message?: unknown
  error?: unknown
  statusCode?: unknown
}

export type AdminApiError = Error & {
  status?: number
  data?: unknown
  responseText?: string
}

const INVALID_ADMIN_TOKEN_CODE = 'invalid_admin_token'
const ADMIN_SESSION_EXPIRED_CODE = 'admin_session_expired'

function readPayloadMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const payload = data as AdminApiErrorPayload
  const message = Array.isArray(payload.message) ? payload.message.join(' ') : payload.message
  return typeof message === 'string' ? message : ''
}

function readPayloadError(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const payload = data as AdminApiErrorPayload
  return typeof payload.error === 'string' ? payload.error : ''
}

export function isAdminAuthMissing(authToken: string | null | undefined) {
  return !authToken || !authToken.trim()
}

export function createAdminAuthHeaders(authToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${authToken}`,
  }
}

export function isAdminSessionExpiredError(error: unknown) {
  if (!error || typeof error !== 'object') return false

  const maybeError = error as Partial<AdminApiError>
  const status = Number(maybeError.status || 0)

  return status === 401
}

export function getAdminApiErrorStatus(error: unknown) {
  if (!error || typeof error !== 'object') return null
  const maybeError = error as Partial<AdminApiError>
  const status = Number(maybeError.status || 0)
  return Number.isInteger(status) && status > 0 ? status : null
}

export function isAdminNetworkError(error: unknown) {
  if (!(error instanceof Error)) return false
  return /failed to fetch|networkerror|load failed/i.test(error.message)
}

export function getAdminApiErrorMessage(error: unknown) {
  if (isAdminSessionExpiredError(error)) {
    const maybeError = error as Partial<AdminApiError>
    const payloadMessage = readPayloadMessage(maybeError.data)
    const payloadError = readPayloadError(maybeError.data)
    const text = [maybeError.message, maybeError.responseText, payloadMessage, payloadError]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return text.includes(INVALID_ADMIN_TOKEN_CODE)
      ? INVALID_ADMIN_TOKEN_CODE
      : ADMIN_SESSION_EXPIRED_CODE
  }

  if (error && typeof error === 'object') {
    const maybeError = error as Partial<AdminApiError>
    const payloadMessage = readPayloadMessage(maybeError.data)
    if (payloadMessage) return payloadMessage
    if (maybeError.message) return maybeError.message
    if (maybeError.responseText) return maybeError.responseText
  }

  return String(error || 'unknown_error')
}

export async function createAdminApiError(response: Response, requestLabel: string) {
  const text = await response.text().catch(() => '')
  let data: unknown = null

  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  const error = new Error(
    `HTTP ${response.status} ${response.statusText} on ${requestLabel}${text ? `: ${text}` : ''}`,
  ) as AdminApiError
  error.status = response.status
  error.data = data
  error.responseText = text

  return error
}
